import * as Sentry from "@sentry/react";
import { motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  type ChatKitHandle,
  ChatWindow,
} from "@krea/shared/features/chat/ChatWindow";
import { useIsMobile } from "@krea/shared/hooks/useIsMobile";

import settings from "@/settings";

import { AssistantLauncher } from "./components/AssistantLauncher";
import { AssistantPanel } from "./components/AssistantPanel";
import { MORPH_DURATION_S } from "./constants";
import { useSuggestions } from "./suggestions";

const PANEL_W = 400;
// Gap from the content area's left edge (launcher + panel share this left edge).
const DOCK_MARGIN = 16;
const BUTTON_BOTTOM = 24;
const PANEL_BOTTOM = 16;
// The panel opens at ~half the content height and expands toward (not quite) full.
const COLLAPSED_RATIO = 0.5;
const EXPANDED_RATIO = 0.9;
const CHAT_ENDPOINT = `${settings.kreaBaseApiUrl}/api/v1/chatkit/lender/chat`;
const KREA_PURPLE = "#39008C";
// Show the error state if ChatKit never signals ready (e.g. the CDN script fails to load).
const READY_TIMEOUT_MS = 15000;
const MORPH_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

// The launcher + panel dock to the bottom-LEFT of the lender-application detail content (not the
// viewport), so they clear the app's left nav and track it when it collapses. We read the content
// region from the shared scroll container the layout already mounts.
const CONTENT_SELECTOR = "#main-content-scroll-container";

interface IContentBounds {
  /** Left edge of the content area in viewport px (0 when it can't be measured). */
  leftPx: number;
  /** Visible height of the content area in px (drives the panel's 50% / 90%). */
  heightPx: number;
}

const readContentBounds = (): IContentBounds => {
  if (typeof document !== "undefined") {
    const el = document.querySelector<HTMLElement>(CONTENT_SELECTOR);
    if (el) {
      return {
        leftPx: el.getBoundingClientRect().left,
        heightPx: el.clientHeight,
      };
    }
  }
  const vh = typeof window !== "undefined" ? window.innerHeight : 0;
  return { leftPx: 0, heightPx: vh };
};

// Track the content area's left/height. ResizeObserver catches the sidebar collapse (the container
// resizes); window resize covers the rest. No scroll listener — the container itself is fixed in
// the layout (only its inner content scrolls), so its rect is scroll-stable.
const useContentBounds = (): IContentBounds => {
  const [bounds, setBounds] = useState<IContentBounds>(readContentBounds);

  useEffect(() => {
    // Synchronous on mount — panel must be positioned correctly from the start.
    setBounds(readContentBounds());

    // Debounce ongoing resize/observer callbacks to avoid jank during sidebar
    // CSS transitions (~200ms). Timer matches the sidebar animation duration.
    let debounceId: number | undefined;
    const measure = () => {
      window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => setBounds(readContentBounds()), 200);
    };

    const el = document.querySelector<HTMLElement>(CONTENT_SELECTOR);
    let observer: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    window.addEventListener("resize", measure);
    return () => {
      window.clearTimeout(debounceId);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return bounds;
};

// Report a chat failure to Sentry without blanking the panel — used for inline runtime errors and
// for the fire-and-forget ChatKit handle calls (so a rejected iframe RPC isn't an unhandled reject).
const reportChatError = (error: unknown) => {
  Sentry.captureException(error, { tags: { feature: "case-assistant" } });
};

type MorphRect = {
  left: number;
  top: number;
  width: number;
  height: number;
  radius: number;
};

/** Resting rect of the open desktop panel, in `fixed` (viewport) coordinates. */
export type PanelRect = {
  left: number;
  /** Distance from the viewport bottom. */
  bottom: number;
  height: number;
  width: number;
};

interface ICaseAssistantProps {
  lenderCompanyId: string;
  lenderApplicationId: string;
  countryCode: string;
  companyName: string;
  orgNumber: string;
}

export const CaseAssistant = ({
  lenderCompanyId,
  lenderApplicationId,
  countryCode,
  companyName,
  orgNumber,
}: ICaseAssistantProps) => {
  const suggestions = useSuggestions();
  const isMobile = useIsMobile(); // 768px (shared MOBILE_BREAKPOINT — the repo-wide standard)
  const reduceMotion = useReducedMotion();
  const { leftPx, heightPx } = useContentBounds();

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const [returnFocus, setReturnFocus] = useState(false);
  const [morph, setMorph] = useState<{ from: MorphRect; to: MorphRect } | null>(
    null,
  );
  // Bumped on retry to change ChatWindow's key → fresh ChatKit instance + pre-flight, instead of
  // relying on the error branch happening to unmount it.
  const [retryNonce, setRetryNonce] = useState(0);

  const triggerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<ChatKitHandle | null>(null);
  // Morph→open hand-off guards: openingRef stops a late onAnimationComplete from re-opening a
  // panel the user already closed; openTimerRef is a fallback so the panel still opens if motion's
  // onAnimationComplete is ever missed (keeps the old unconditional-open guarantee).
  const openingRef = useRef(false);
  const openTimerRef = useRef<number | null>(null);

  // Bottom-left resting rect of the open panel. Expand grows it upward (bottom stays put). The same
  // numbers drive the morph target below so the placeholder lands exactly on the panel.
  const panelHeight = Math.round(
    (expanded ? EXPANDED_RATIO : COLLAPSED_RATIO) * heightPx,
  );
  const panelRect: PanelRect = {
    left: leftPx + DOCK_MARGIN,
    bottom: PANEL_BOTTOM,
    height: panelHeight,
    width: PANEL_W,
  };

  const revealPanel = useCallback(() => {
    if (!openingRef.current) return;
    openingRef.current = false;
    if (openTimerRef.current != null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    setMorph(null);
    setOpen(true);
  }, []);

  useEffect(
    () => () => {
      if (openTimerRef.current != null)
        window.clearTimeout(openTimerRef.current);
    },
    [],
  );

  const targetRect = useCallback((): MorphRect => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (isMobile) {
      const h = Math.min(vh * 0.86, 760);
      return { left: 0, top: vh - h, width: vw, height: h, radius: 18 };
    }
    // Bottom-left dock; mirrors panelRect (same primitives) so the morph lands on the panel.
    return {
      left: leftPx + DOCK_MARGIN,
      top: vh - PANEL_BOTTOM - panelHeight,
      width: PANEL_W,
      height: panelHeight,
      radius: 14,
    };
  }, [isMobile, leftPx, panelHeight]);

  const doOpen = useCallback(() => {
    setError(false);
    setReady(false);
    setReturnFocus(false);
    // Always open at the collapsed (~50%) height; the user expands afterwards.
    setExpanded(false);

    const rect = triggerRef.current?.getBoundingClientRect();
    if (reduceMotion || !rect) {
      setMorph(null);
      setOpen(true);
      return;
    }

    // Animate a placeholder box from the launcher pill to the panel's resting rect, then reveal
    // the real panel (its own entrance covers the hand-off). The box is separate from the panel
    // so the cross-origin ChatKit iframe isn't reflowed every frame. motion handles the timing
    // and respects prefers-reduced-motion (see reduceMotion above), replacing manual setTimeouts.
    openingRef.current = true;
    setMorph({
      from: {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        radius: 999,
      },
      to: targetRect(),
    });
    if (openTimerRef.current != null) window.clearTimeout(openTimerRef.current);
    openTimerRef.current = window.setTimeout(
      revealPanel,
      MORPH_DURATION_S * 1000 + 150,
    );
  }, [reduceMotion, targetRect, revealPanel]);

  const handleClose = useCallback(() => {
    openingRef.current = false;
    if (openTimerRef.current != null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    setMorph(null);
    setOpen(false);
    setExpanded(false);
    setReady(false);
    // Drop the handle from the unmounting ChatKit instance so a stray New Chat during the next
    // open's loading window can't drive a dead instance.
    handleRef.current = null;
    setReturnFocus(true);
  }, []);

  // Bumping retryNonce changes ChatWindow's key, forcing a fresh ChatKit instance + pre-flight
  // (explicit, rather than relying on the error branch happening to unmount it). A pending question
  // (set before the failure) is intentionally kept so the retry resends it; handleClose clears it,
  // so giving up instead of retrying never resurfaces the question.
  const handleRetry = useCallback(() => {
    setError(false);
    setReady(false);
    setRetryNonce((n) => n + 1);
  }, []);

  const handleToggleExpand = useCallback(() => setExpanded((v) => !v), []);

  const onChatReady = useCallback((handle: ChatKitHandle) => {
    handleRef.current = handle;
    setReady(true);
    handle.focusComposer();
  }, []);

  const onChatError = useCallback(
    (
      chatError: unknown,
      meta?: { phase?: "preflight" | "ready-timeout" | "runtime" },
    ) => {
      // A post-ready ("runtime") failure is rendered inline by ChatKit inside the iframe, so
      // don't blank the whole panel — just report it to Sentry. Pre-flight and never-ready faults
      // (the panel never became usable) get the full error screen + retry. The never-ready timeout
      // lives in ChatWindow as the opt-in `readyTimeout` prop (below); only this lender surface
      // passes it today, so customer/admin would need to opt in to get the same backstop.
      if (meta?.phase === "runtime") {
        reportChatError(chatError);
        return;
      }
      setError(true);
    },
    [],
  );

  // Cmd/Ctrl+I opens (focuses composer on ready); Esc closes.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        (event.key === "i" || event.key === "I")
      ) {
        event.preventDefault();
        if (!open) {
          doOpen();
        } else {
          handleRef.current?.focusComposer();
        }
      } else if (event.key === "Escape" && open) {
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, doOpen, handleClose]);

  const chatOptions = useMemo(
    () => ({
      // Keep the theme minimal: ONLY the accent + light scheme. Customer/admin pass no theme and
      // render fine, so anything beyond the accent is a regression risk inside the cross-origin
      // iframe. A partial `color.surface` override (without a matching grayscale palette) produced a
      // dark, unreadable composer, and `typography.fontFamily: "Roobert"` can't load cross-origin
      // without `fontSources` — both are removed here. (Roobert via fontSources = future follow-up.)
      theme: {
        colorScheme: "light" as const,
        color: {
          accent: { primary: KREA_PURPLE, level: 1 as const },
        },
        radius: "round" as const,
        density: "normal" as const,
      },
      startScreen: {
        prompts: suggestions.map((s) => ({ label: s.label, prompt: s.prompt })),
      },
      composer: { attachments: { enabled: false } },
    }),
    [suggestions],
  );

  // The backend scopes the case via both headers — X-Lender-Application-Id identifies the
  // LenderApplication PK and X-Lender-Company-Id scopes the request to the lender company the
  // user is currently acting as. Both are required; the backend returns 400 if either is absent.
  const requestHeaders = useMemo(
    () => ({
      "X-Lender-Application-Id": lenderApplicationId,
      "X-Lender-Company-Id": lenderCompanyId,
    }),
    [lenderApplicationId, lenderCompanyId],
  );

  const brandName = countryCode === "FI" ? "Kredita" : "Krea";

  return createPortal(
    <>
      {!open && (
        <AssistantLauncher
          triggerRef={triggerRef}
          anchorLeftPx={leftPx + DOCK_MARGIN}
          anchorBottomPx={BUTTON_BOTTOM}
          autoFocus={returnFocus}
          onOpen={doOpen}
        />
      )}

      {morph && (
        <motion.div
          aria-hidden
          // Animate only transform (x/y/scale) + border-radius, so the morph stays on the
          // compositor and never triggers per-frame layout. The box is laid out at the panel's
          // target rect (static width/height) and, via a top-left origin, starts translated+scaled
          // onto the launcher pill, then releases to identity (the resting panel rect).
          className="fixed left-0 top-0 z-[400003] bg-primary shadow-[0_24px_60px_-16px_rgba(0,0,0,0.32)]"
          style={{
            width: morph.to.width,
            height: morph.to.height,
            transformOrigin: "top left",
          }}
          initial={{
            x: morph.from.left,
            y: morph.from.top,
            scaleX: morph.from.width / morph.to.width,
            scaleY: morph.from.height / morph.to.height,
            borderRadius: morph.from.radius,
          }}
          animate={{
            x: morph.to.left,
            y: morph.to.top,
            scaleX: 1,
            scaleY: 1,
            borderRadius: morph.to.radius,
          }}
          transition={{ duration: MORPH_DURATION_S, ease: MORPH_EASE }}
          onAnimationComplete={revealPanel}
        />
      )}

      {open && (
        <AssistantPanel
          isMobile={isMobile}
          rect={panelRect}
          expanded={expanded}
          companyName={companyName}
          orgNumber={orgNumber}
          title="Case Assistant"
          ready={ready}
          error={error}
          labels={{
            expand: "Expand panel",
            collapse: "Collapse panel",
            close: "Close",
            errorBody: `The assistant isn't available for this case right now. Please refresh or contact the responsible agent at ${brandName}.`,
            retry: "Try again",
            loading: "Loading…",
          }}
          onClose={handleClose}
          onToggleExpand={handleToggleExpand}
          onRetry={handleRetry}
        >
          <ChatWindow
            key={`${lenderApplicationId}:${retryNonce}`}
            endpoint={CHAT_ENDPOINT}
            countryCode={countryCode}
            locale="en"
            title="Ask about this case"
            placeholder="Ask Case Assistant"
            requestHeaders={requestHeaders}
            resumeStrategy="session-start"
            options={chatOptions}
            readyTimeout={READY_TIMEOUT_MS}
            onReady={onChatReady}
            onError={onChatError}
          />
        </AssistantPanel>
      )}
    </>,
    document.body,
  );
};
